/**
 * Parent-portal launcher (ADR-0015) — the Office-365-style app grid for "one
 * login, many apps". Lists the apps the signed-in end-user can open
 * (`GET /me/apps`), each tile linking to that app's home (resolved via the
 * entry route). Wears the TENANT brand (`getParentBrand`). An empty grid is a
 * real state (a credentialed user with zero memberships, choice #2).
 *
 * Requires a portal session — anonymous visitors bounce to the parent login.
 */
import { useQuery } from "@tanstack/react-query";
import { ChevronRight, LayoutGrid, Loader2, LogOut, ShieldCheck } from "lucide-react";
import { Link, Navigate, useNavigate } from "react-router-dom";

import { TwoFactorPanel } from "@/components/auth/totp";
import { portalApi, type MyAppOut } from "@/lib/api-portal";
import { portalTotpAdapter } from "@/lib/api-2fa";
import {
  parentLoginPath,
  portalHomePath,
  usePortalAuth,
} from "@/lib/portalAuth";

function initial(name: string): string {
  return (name.trim()[0] || "?").toUpperCase();
}

function AppTile({ app }: { app: MyAppOut }) {
  const accent = app.theme.accent || "#0f172a";
  return (
    <Link
      to={portalHomePath(app.slug)}
      className="group relative flex items-center gap-4 overflow-hidden rounded-xl border border-border bg-surface p-4 shadow-sm transition-all duration-150 hover:-translate-y-0.5 hover:border-foreground/15 hover:shadow-md"
    >
      {/* Accent wash that warms on hover — a hint of the app's colour. */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-y-0 left-0 w-1 opacity-70 transition-opacity group-hover:opacity-100"
        style={{ background: accent }}
      />
      {/* App icon — a squared mark (logo or accent initial). */}
      {app.logo_url ? (
        <img
          src={app.logo_url}
          alt=""
          className="h-12 w-12 shrink-0 rounded-xl object-contain shadow-sm ring-1 ring-black/5"
        />
      ) : (
        <div
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl text-base font-semibold text-white shadow-sm ring-1 ring-black/5"
          style={{ background: accent }}
        >
          {initial(app.name)}
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold text-foreground">{app.name}</div>
        <span className="mt-1 inline-flex items-center rounded-md bg-muted px-2 py-0.5 text-[11px] font-medium capitalize text-muted-foreground">
          {app.role}
        </span>
      </div>
      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/40 transition-all group-hover:translate-x-0.5 group-hover:text-muted-foreground" />
    </Link>
  );
}

export function ParentPortalLauncher() {
  const navigate = useNavigate();
  const token = usePortalAuth((s) => s.token);
  const user = usePortalAuth((s) => s.user);
  const logoutLocal = usePortalAuth((s) => s.logout);

  const brandQ = useQuery({
    queryKey: ["parent-brand"],
    queryFn: () => portalApi.getParentBrand(),
    staleTime: 5 * 60_000,
    enabled: !!token,
  });
  const appsQ = useQuery({
    queryKey: ["my-apps"],
    queryFn: () => portalApi.myApps(),
    enabled: !!token,
  });

  // Anonymous → parent login (token is the only gate; the feed is per-token).
  if (!token) {
    return <Navigate to={parentLoginPath()} replace />;
  }

  async function onSignOut() {
    try {
      await portalApi.parentLogout();
    } catch {
      /* best-effort — drop the client session regardless */
    }
    logoutLocal();
    navigate(parentLoginPath(), { replace: true });
  }

  const brand = brandQ.data;

  const tenantName = brand?.name || "Your apps";
  const accent = brand?.theme.accent || undefined;
  const pageBg = brand?.theme.background || undefined;
  const apps = appsQ.data ?? [];
  const firstName = user?.full_name?.trim().split(" ")[0];

  return (
    <div
      className="tray-light flex min-h-screen flex-col bg-background"
      style={pageBg ? { background: pageBg } : undefined}
    >
      {/* Tenant header. */}
      <header className="flex items-center justify-between border-b border-border bg-surface px-6 py-3.5">
        <div className="flex items-center gap-3">
          {brand?.logo_url ? (
            <img src={brand.logo_url} alt={tenantName} className="h-8 max-w-[160px] object-contain" />
          ) : (
            <span className="text-base font-semibold tracking-tight text-foreground">
              {tenantName}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {user && (
            <div className="flex items-center gap-2">
              <div
                className="flex h-7 w-7 items-center justify-center rounded-md text-xs font-semibold text-white"
                style={{ background: accent || "#0f172a" }}
              >
                {initial(user.full_name || user.email)}
              </div>
              <span className="hidden text-sm text-muted-foreground sm:inline">
                {user.full_name || user.email}
              </span>
            </div>
          )}
          <button
            type="button"
            onClick={onSignOut}
            title="Sign out"
            className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm text-muted-foreground transition hover:bg-muted hover:text-foreground"
          >
            <LogOut className="h-4 w-4" />
            <span className="hidden sm:inline">Sign out</span>
          </button>
        </div>
      </header>

      {/* Top-anchored content — a launcher reads as a workspace, not an island
          floating in the middle of an empty page. */}
      <main className="flex-1 overflow-y-auto px-6 py-14 sm:py-20">
        <div className="mx-auto w-full max-w-2xl">
          <div className="mb-7">
            <h1 className="text-2xl font-semibold tracking-tight text-foreground">
              {firstName ? `Welcome back, ${firstName}` : "Your apps"}
            </h1>
            <p className="mt-1.5 text-sm text-muted-foreground">
              {apps.length > 0
                ? `You have access to ${apps.length} ${apps.length === 1 ? "app" : "apps"}. Open one — you stay signed in across all of them.`
                : "Choose an app to open — you stay signed in across all of them."}
            </p>
          </div>

          {appsQ.isPending ? (
            <div className="flex items-center justify-center py-16 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : apps.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border bg-surface/60 px-6 py-16 text-center">
              <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-xl bg-muted">
                <LayoutGrid className="h-5 w-5 text-muted-foreground/60" />
              </div>
              <p className="mt-3 text-sm font-medium text-foreground">No apps yet</p>
              <p className="mt-1 text-sm text-muted-foreground">
                You don't have access to any app. An administrator will add you to one.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {apps.map((app) => (
                <AppTile key={app.slug} app={app} />
              ))}
            </div>
          )}

          {/* Account security — self-service 2FA (ADR-0017). */}
          <div className="mt-10 rounded-xl border border-border bg-surface p-5">
            <div className="mb-3 flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-muted-foreground" />
              <h2 className="text-sm font-semibold text-foreground">Sicurezza</h2>
            </div>
            <TwoFactorPanel adapter={portalTotpAdapter} />
          </div>
        </div>
      </main>

      {/* Optional tenant footer line (ADR-0015) — shown on login + launcher. */}
      {brand?.footer && (
        <footer className="border-t border-border bg-surface px-6 py-3 text-center text-xs text-muted-foreground">
          {brand.footer}
        </footer>
      )}
    </div>
  );
}
